/**
 * Self-contained voice recording bar for both hold and locked modes.
 *
 * How it works:
 * 1. In "hold" mode (user pressing mic): shows red dot + duration + "‹ Slide to cancel" + mic button
 * 2. In "locked" mode (hands-free): shows red dot + duration + waveform + send/cancel buttons
 * 3. Slide-to-cancel text fades as user drags left (driven by slideProgress)
 * 4. Lock icon above mic animates upward as user drags up (driven by lockProgress)
 *
 * Key features:
 * - Animated bouncing chevron on "Slide to cancel"
 * - Lock pill with padlock + chevron-up
 * - Red pulsing recording indicator dot
 * - Telegram-style large mic button during hold
 */

import { ChevronLeft, Lock, ChevronUp, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/primitives/button";
import { cn, formatRecordingDuration } from "@/lib/utils";
import { VoiceRecordingWaveForm } from "../VoiceRecordingWaveForm";
import { useTranslation } from "react-i18next";

interface VoiceRecordingBarProps {
  /** Current recording duration in ms */
  duration: number;
  /** Whether recording is near the max duration limit */
  isNearMaxDuration: boolean;
  /** Live amplitude for waveform animation (0–50) */
  liveAmplitude: number;
  /** Whether the recording is in locked (hands-free) mode */
  isLocked: boolean;
  /** 0→1 slide progress toward cancel threshold */
  slideProgress: number;
  /** Called when the user taps send in locked mode */
  onSend: () => void;
  /** Called when the user taps cancel/trash in locked mode */
  onCancel: () => void;
}

// ── Sub-components ──

/** Red pulsing dot that indicates active recording */
function RecordingDot(): JSX.Element {
  return (
    <span className="relative flex size-3 shrink-0">
      <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-500 opacity-75" />
      <span className="relative inline-flex size-3 rounded-full bg-red-500" />
    </span>
  );
}

/** Lock icon pill shown directly above the mic button during hold mode */
export function LockIndicator({
  lockProgress,
}: Readonly<{ lockProgress: number }>) {
  // Translate upward as user drags up, with a max translation
  const translateY = -lockProgress * 40;

  return (
    <div
      className="pointer-events-none absolute bottom-full left-1/2 mb-2 flex flex-col items-center gap-1 rounded-full bg-muted/90 px-3 py-2.5 shadow-lg"
      style={{
        transform: `translate(-50%, ${translateY}px)`,
        opacity: lockProgress > 0 ? 1 : 0.6,
      }}
    >
      <Lock
        className={cn(
          "size-5 transition-colors duration-150",
          lockProgress >= 1 ? "text-primary" : "text-muted-foreground",
        )}
      />
      <ChevronUp className="size-4 animate-bounce text-muted-foreground" />
    </div>
  );
}

// ── Main component ──

export function VoiceRecordingBar({
  duration,
  isNearMaxDuration,
  liveAmplitude,
  isLocked,
  slideProgress,
  onSend,
  onCancel,
}: Readonly<VoiceRecordingBarProps>) {
  if (isLocked) {
    return (
      <LockedRecordingBar
        duration={duration}
        isNearMaxDuration={isNearMaxDuration}
        liveAmplitude={liveAmplitude}
        onSend={onSend}
        onCancel={onCancel}
      />
    );
  }

  return (
    <HoldRecordingBar
      duration={duration}
      isNearMaxDuration={isNearMaxDuration}
      slideProgress={slideProgress}
    />
  );
}

// ── Hold mode: user is pressing the mic button ──

interface HoldRecordingBarProps {
  duration: number;
  isNearMaxDuration: boolean;
  slideProgress: number;
}

function HoldRecordingBar({
  duration,
  isNearMaxDuration,
  slideProgress,
}: Readonly<HoldRecordingBarProps>) {
  const { t } = useTranslation();

  // Fade out "Slide to cancel" text as user drags toward threshold
  const textOpacity = Math.max(1 - slideProgress * 1.5, 0);

  return (
    <div className="flex flex-1 items-center gap-3">
      {/* Red dot + duration */}
      <div className="flex items-center gap-2">
        <RecordingDot />
        <span
          className={cn(
            "min-w-14 select-none text-sm tabular-nums",
            isNearMaxDuration && "animate-pulse text-red-700 duration-700",
          )}
        >
          {formatRecordingDuration(duration)}
        </span>
      </div>

      {/* Slide to cancel — entire group nudges left */}
      <div
        className="flex flex-1 select-none items-center justify-center gap-1"
        style={{
          opacity: textOpacity,
          animation: "bounce-left 1s ease-in-out infinite",
        }}
      >
        <ChevronLeft className="size-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          {t("voiceRecording.slideToCancel")}
        </span>
      </div>
    </div>
  );
}

// ── Locked mode: hands-free recording with send/cancel buttons ──

interface LockedRecordingBarProps {
  duration: number;
  isNearMaxDuration: boolean;
  liveAmplitude: number;
  onSend: () => void;
  onCancel: () => void;
}

function LockedRecordingBar({
  duration,
  isNearMaxDuration,
  liveAmplitude,
  onSend,
  onCancel,
}: Readonly<LockedRecordingBarProps>) {
  return (
    <div className="flex flex-1 items-center gap-3">
      {/* Red dot + duration */}
      <div className="flex items-center gap-2">
        <RecordingDot />
        <span
          className={cn(
            "min-w-14 select-none text-sm tabular-nums",
            isNearMaxDuration && "animate-pulse text-red-700 duration-700",
          )}
        >
          {formatRecordingDuration(duration)}
        </span>
      </div>

      {/* Waveform */}
      <div className="mr-3 flex-1 select-none">
        <VoiceRecordingWaveForm amplitude={liveAmplitude} isRecording />
      </div>

      {/* Cancel + Send buttons */}
      <div className="flex items-center gap-1">
        <Button
          size="icon"
          variant="ghost"
          onClick={onCancel}
          className="size-10 shrink-0 text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="size-5" />
        </Button>
        <Button
          size="icon"
          onClick={onSend}
          className="size-10 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/80"
        >
          <Send className="size-5" />
        </Button>
      </div>
    </div>
  );
}
